import { FunctionComponent, useEffect, useMemo, useState } from "react";
import type { ServiceRequest } from "fhir/r5";
import { ActionPlanAppointmentItem, ActionPlanRequestItem, ActionPlanViewModel } from "../../models/ActionPlan";
import { DecisionSuggestion } from "../../models/DecisionSupport";
import { VALUE_LABELS, ValueCode } from "../../models/OverviewDashboard";
import ActionPlanService from "../../services/ActionPlanService";
import {
    getAppointmentParticipationInfo,
    registerCurrentUserToAppointment
} from "../../services/AppointmentParticipationService";
import { UpCareThemeCode } from "../../utils/UpCareThemes";
import useStableStringArray from "../../hooks/useStableStringArray";
import AppointmentRegistrationModal from "../AppointmentRegistration/AppointmentRegistrationModal";
import ActionPlanRequestDetailModal from "./ActionPlanRequestDetailModal";
import ActionPlanNewActionMenu, { ActionPlanCreationMode } from "./ActionPlanNewActionMenu";
import styles from "./ActionPlanBoardWidget.module.css";

interface ActionPlanBoardWidgetProps {
    fhirBaseUrl: string;
    value: ValueCode;
    theme?: UpCareThemeCode;
    /** Périmètre courant pour les ateliers programmés et les formulaires. */
    organizationReferences: string[];
    /** Périmètre distinct pour les besoins et solutions, par exemple tous les pôles. */
    requestOrganizationReferences?: string[];
    suggestion: DecisionSuggestion;
    poleOrganization?: {
        reference: string;
        name: string;
    };
    onPlanChange?: (plan: ActionPlanViewModel) => void;
    onAppointmentRegistration?: (appointment: ActionPlanAppointmentItem) => void | Promise<void>;
}

const EMPTY_PLAN: ActionPlanViewModel = {
    needs: [],
    proposals: [],
    appointments: []
};

const ActionPlanBoardWidget: FunctionComponent<ActionPlanBoardWidgetProps> = ({
    fhirBaseUrl,
    value,
    theme,
    organizationReferences,
    requestOrganizationReferences,
    suggestion,
    poleOrganization,
    onPlanChange,
    onAppointmentRegistration
}) => {
    const [plan, setPlan] = useState<ActionPlanViewModel>(EMPTY_PLAN);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>();
    const [selectedAppointment, setSelectedAppointment] = useState<ActionPlanAppointmentItem>();
    const [selectedRequest, setSelectedRequest] = useState<ActionPlanRequestItem>();
    const [refreshVersion, setRefreshVersion] = useState(0);

    const service = useMemo(
        () => new ActionPlanService({ fhirBaseUrl }),
        [fhirBaseUrl]
    );

    const stableOrganizationReferences = useStableStringArray(organizationReferences);
    const stableRequestOrganizationReferences = useStableStringArray(requestOrganizationReferences ?? organizationReferences);
    const hasRequestOrganizationReferences = requestOrganizationReferences !== undefined;

    useEffect(() => {
        let mounted = true;

        if (stableOrganizationReferences.length === 0 && (!hasRequestOrganizationReferences || stableRequestOrganizationReferences.length === 0)) {
            setPlan(EMPTY_PLAN);
            onPlanChange?.(EMPTY_PLAN);
            setLoading(false);
            setError(undefined);
            return () => {
                mounted = false;
            };
        }

        setLoading(true);
        setError(undefined);

        service.search({
            value,
            theme,
            organizationReferences: stableOrganizationReferences,
            requestOrganizationReferences: hasRequestOrganizationReferences ? stableRequestOrganizationReferences : undefined
        })
            .then((result) => {
                if (mounted) {
                    setPlan(result);
                    onPlanChange?.(result);
                }
            })
            .catch((reason) => {
                console.error(reason);
                if (mounted) {
                    setPlan(EMPTY_PLAN);
                    onPlanChange?.(EMPTY_PLAN);
                    setError("Impossible de charger le plan d'action.");
                }
            })
            .finally(() => {
                if (mounted) setLoading(false);
            });

        return () => {
            mounted = false;
        };
    }, [service, value, theme, stableOrganizationReferences, stableRequestOrganizationReferences, hasRequestOrganizationReferences, refreshVersion, onPlanChange]);

    const refreshPlan = () => setRefreshVersion((current) => current + 1);

    const handleRequestCreated = (request: ServiceRequest, mode: ActionPlanCreationMode) => {
        const item = service.mapRequest(request);

        if (!item.valueCodes.includes(value) || (theme && item.themeCode !== theme)) {
            return;
        }

        setPlan((current) => {
            const withoutDuplicate = (items: typeof current.needs) =>
                items.filter((candidate) => candidate.id !== item.id);

            const updated: ActionPlanViewModel = mode === "need"
                ? {
                    ...current,
                    needs: [item, ...withoutDuplicate(current.needs)]
                }
                : {
                    ...current,
                    proposals: [item, ...withoutDuplicate(current.proposals)]
                };

            onPlanChange?.(updated);
            return updated;
        });
    };

    const handleRegistrationConfirm = async (appointment: ActionPlanAppointmentItem) => {
        if (onAppointmentRegistration) {
            await onAppointmentRegistration(appointment);
            setSelectedAppointment(undefined);
            refreshPlan();
            return;
        }

        const persisted = await registerCurrentUserToAppointment(fhirBaseUrl, appointment.raw);
        const participation = getAppointmentParticipationInfo(persisted);
        const updatedAppointment: ActionPlanAppointmentItem = {
            ...appointment,
            raw: persisted,
            participantCount: participation.participantCount,
            participantCapacity: participation.participantCapacity,
            isRegistered: participation.isRegistered
        };

        const updatedPlan = {
            ...plan,
            appointments: plan.appointments.map((item) =>
                item.id === appointment.id ? updatedAppointment : item
            )
        };
        setPlan(updatedPlan);
        onPlanChange?.(updatedPlan);
        setSelectedAppointment(undefined);
    };

    return (
        <section className={styles.board} aria-label="Plan d'action en colonnes Kanban">
            {loading && <p className={styles.state}>Chargement du plan d'action…</p>}
            {error && <p className={styles.error}>{error}</p>}

            {!loading && !error && (
                <div className={styles.columns}>
                    <ActionColumn
                        title="Besoins déclarés"
                        count={plan.needs.length}
                        icon="▣"
                        tone="need"
                        highlighted={suggestion.targetColumn === "needs"}
                        action={(
                            <ActionPlanNewActionMenu
                                fhirBaseUrl={fhirBaseUrl}
                                value={value}
                                organizationReferences={organizationReferences}
                                poleOrganization={poleOrganization}
                                triggerMode="need"
                                triggerLabel="Je déclare un besoin"
                                onCreated={handleRequestCreated}
                            />
                        )}
                    >
                        {plan.needs.map((item) => (
                            <RequestCard
                                key={item.id}
                                item={item}
                                fallbackValue={value}
                                kind="need"
                                deletable
                                onOpen={setSelectedRequest}
                            />
                        ))}
                    </ActionColumn>

                    <ActionColumn
                        title="Solutions proposées"
                        count={plan.proposals.length}
                        icon="☆"
                        tone="proposal"
                        highlighted={suggestion.targetColumn === "proposals"}
                        action={(
                            <ActionPlanNewActionMenu
                                fhirBaseUrl={fhirBaseUrl}
                                value={value}
                                organizationReferences={organizationReferences}
                                poleOrganization={poleOrganization}
                                triggerMode="proposal"
                                triggerLabel="Proposer une solution"
                                onCreated={handleRequestCreated}
                            />
                        )}
                    >
                        {plan.proposals.map((item) => (
                            <RequestCard
                                key={item.id}
                                item={item}
                                fallbackValue={value}
                                kind="proposal"
                                deletable
                                onOpen={setSelectedRequest}
                            />
                        ))}
                    </ActionColumn>

                    <ActionColumn
                        title="Ateliers programmés"
                        count={plan.appointments.length}
                        icon="▣"
                        tone="appointment"
                    >
                        {plan.appointments.map((item) => {
                            const primaryValue = getPrimaryValue(item.valueCodes, value);
                            const full = item.participantCapacity !== undefined &&
                                (item.participantCount ?? 0) >= item.participantCapacity;

                            return (
                                <article
                                    key={item.id}
                                    className={styles.appointmentCard}
                                    data-value={primaryValue}
                                    onClick={() => setSelectedAppointment(item)}
                                >
                                    <div className={styles.cardTopRow}>
                                        <ValueBadge value={primaryValue} />
                                        <span className={styles.appointmentType}>{item.type ?? "Atelier / formation"}</span>
                                    </div>
                                    <strong>{item.title}</strong>
                                    {item.theme && <span className={styles.themeBadge}>{item.theme}</span>}
                                    {item.presenter && <span className={styles.presenter}>par {item.presenter}</span>}

                                    <div className={styles.appointmentSummary}>
                                        {item.start && (
                                            <div className={styles.dateTile}>
                                                <strong>{formatDay(item.start)}</strong>
                                                <span>{formatMonth(item.start)}</span>
                                                <small>{formatTime(item.start, item.end)}</small>
                                            </div>
                                        )}
                                        <div className={styles.participantTile}>
                                            <span aria-hidden="true">♙</span>
                                            <strong>
                                                {item.participantCount ?? 0}
                                                {item.participantCapacity !== undefined ? `/${item.participantCapacity}` : ""}
                                            </strong>
                                            <small>inscrits</small>
                                        </div>
                                    </div>

                                    {item.isRegistered ? (
                                        <div className={styles.registered}>✓ Inscrit</div>
                                    ) : full ? (
                                        <div className={styles.full}>Complet</div>
                                    ) : (
                                        <button
                                            type="button"
                                            className={styles.registerButton}
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                setSelectedAppointment(item);
                                            }}
                                        >
                                            Je m'inscris
                                        </button>
                                    )}
                                </article>
                            );
                        })}
                    </ActionColumn>
                </div>
            )}

            <AppointmentRegistrationModal
                open={!!selectedAppointment}
                appointment={selectedAppointment}
                onClose={() => setSelectedAppointment(undefined)}
                onConfirm={handleRegistrationConfirm}
            />
            <ActionPlanRequestDetailModal
                open={!!selectedRequest}
                request={selectedRequest}
                onClose={() => setSelectedRequest(undefined)}
            />
        </section>
    );
};

const RequestCard: FunctionComponent<{
    item: ActionPlanRequestItem;
    fallbackValue: ValueCode;
    kind: "need" | "proposal";
    deletable?: boolean;
    onOpen: (item: ActionPlanRequestItem) => void;
}> = ({ item, fallbackValue, kind, deletable = false, onOpen }) => {
    const open = () => onOpen(item);

    return (
        <article
            className={`${styles.itemCard} ${styles.clickableCard}`}
            role="button"
            tabIndex={0}
            aria-label={`Voir le détail de ${item.title}`}
            onClick={open}
            onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    open();
                }
            }}
        >
            <div className={styles.cardTopRow}>
                <ValueBadge value={getPrimaryValue(item.valueCodes, fallbackValue)} />
                <div className={styles.cardControls}>
                    {item.status && <span className={styles.status}>{item.status}</span>}
                    {deletable && <DeleteButton />}
                </div>
            </div>
            <strong>{item.title}</strong>
            {item.theme && <span className={styles.themeBadge}>{item.theme}</span>}
            {item.organizationDisplay && (
                <span className={styles.requestOrganization}>
                    {kind === "need" ? "Besoin exprimé par " : "Proposé par "}
                    <span className={styles.requestOrganizationName}>{item.organizationDisplay}</span>
                </span>
            )}
        </article>
    );
};

const DeleteButton: FunctionComponent = () => (
    <button
        type="button"
        className={styles.deleteButton}
        aria-label="Suppression bientôt disponible"
        title="La suppression sera bientôt disponible"
        disabled
        onClick={(event) => event.stopPropagation()}
    >
        <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M4 7h16M10 11v6m4-6v6M9 7l1-2h4l1 2m-9 0 1 13h10l1-13" />
        </svg>
    </button>
);

const ActionColumn: FunctionComponent<{
    title: string;
    count: number;
    icon: string;
    tone: "need" | "proposal" | "appointment";
    highlighted?: boolean;
    action?: React.ReactNode;
    children: React.ReactNode;
}> = ({ title, count, icon, tone, highlighted, action, children }) => (
    <section className={`${styles.column} ${highlighted ? styles.highlighted : ""}`}>
        <header className={styles.columnHeader}>
            <span className={`${styles.columnIcon} ${styles[tone]}`} aria-hidden="true">{icon}</span>
            <h2>{title}</h2>
            <span className={styles.count}>{count}</span>
        </header>
        <div className={styles.items}>
            {count === 0 ? <p className={styles.empty}>Aucun élément pour le moment.</p> : children}
        </div>
        {action && <div className={styles.columnAction}>{action}</div>}
    </section>
);

const ValueBadge: FunctionComponent<{ value: ValueCode }> = ({ value }) => (
    <span className={styles.valueBadge} data-value={value}>{VALUE_LABELS[value]}</span>
);

const getPrimaryValue = (values: string[], fallback: ValueCode): ValueCode => {
    const first = values[0] as ValueCode | undefined;
    return first ?? fallback;
};

const formatDay = (value: string): string => new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit"
}).format(new Date(value));

const formatMonth = (value: string): string => new Intl.DateTimeFormat("fr-FR", {
    month: "short"
}).format(new Date(value)).replace(".", "");

const formatTime = (start: string, end?: string): string => {
    const formatter = new Intl.DateTimeFormat("fr-FR", { hour: "2-digit", minute: "2-digit" });
    return end
        ? `${formatter.format(new Date(start))}–${formatter.format(new Date(end))}`
        : formatter.format(new Date(start));
};

export default ActionPlanBoardWidget;
